import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Flame, 
  Sparkles, 
  Calendar, 
  Clock, 
  ArrowRight, 
  User, 
  TrendingUp, 
  BookOpen, 
  FileText, 
  ChevronRight,
  ShieldCheck,
  Building2
} from 'lucide-react';
import { db, collection, query, where, onSnapshot, orderBy } from '@/firebase';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDateSafe } from '@/utils/formatters';

export interface ArticleItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  imageUrl: string;
  category: string;
  authorName: string;
  createdAt: any;
  status: string;
  views?: number;
  isPopular?: boolean;
}

const FALLBACK_ARTICLES: ArticleItem[] = [
  {
    id: 'art-lead-1',
    title: 'Panduan Lengkap Penyusunan Dokumen SMKK & RKK Standar Permen PUPR 10/2021',
    slug: 'panduan-penyusunan-smkk-rkk',
    excerpt: 'Pelajari sistematika baku, format IBPRP (Identifikasi Bahaya & Pengendalian Risiko), serta tips jitu mengisi RKK agar berkas administrasi teknis lolos evaluasi tender LPSE.',
    imageUrl: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1200&q=80',
    category: 'Regulasi & SMKK',
    authorName: 'Tim Ahli Azman204',
    createdAt: null,
    status: 'published',
    views: 3420,
    isPopular: true
  },
  {
    id: 'art-pop-2',
    title: 'Strategi Mengurus SBU & SKK Konstruksi Melalui Portal OSS RBA Tanpa Kendala',
    slug: 'sertifikat-badan-usaha-sbu',
    excerpt: 'Panduan step-by-step pemenuhan persyaratan Sertifikat Badan Usaha (SBU) dan Sertifikat Kompetensi Kerja (SKK) untuk kualifikasi tender pemerintah.',
    imageUrl: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=800&q=80',
    category: 'Tender & Legalitas',
    authorName: 'Konsultan Legalitas',
    createdAt: null,
    status: 'published',
    views: 2890,
    isPopular: true
  },
  {
    id: 'art-pop-3',
    title: 'Tips Otomatisasi Perhitungan RAB & AHSP Menggunakan Spreadsheet Terintegrasi',
    slug: 'mengelola-rab-proyek-excel',
    excerpt: 'Cegah risiko pembengkakan anggaran (cost overrun) dengan template excel RAB yang dilengkapi rumus rekapitulasi upah, bahan, dan sewa alat otomatis.',
    imageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80',
    category: 'Manajemen RAB',
    authorName: 'Estimator Proyek',
    createdAt: null,
    status: 'published',
    views: 2150,
    isPopular: true
  },
  {
    id: 'art-pop-4',
    title: 'Studi Kasus: Evaluasi Dokumen Penawaran Teknis yang Kerap Gugur di LPSE SPSE',
    slug: 'evaluasi-dokumen-penawaran-lpse',
    excerpt: 'Analisis mendalam kesalahan umum kontraktor pemula pada tahap pembuktian kualifikasi dan cara mengantisipasinya secara tepat.',
    imageUrl: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=800&q=80',
    category: 'Tender & Legalitas',
    authorName: 'Auditor Pengadaan',
    createdAt: null,
    status: 'published',
    views: 1940,
    isPopular: false
  }
];

export default function HomeNewsSection() {
  const [articles, setArticles] = useState<ArticleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'popular' | 'smkk' | 'legalitas' | 'rab'>('all');

  useEffect(() => {
    let unsubscribe = () => {};
    try {
      const q = query(
        collection(db, 'articles'),
        where('status', '==', 'published'),
        orderBy('createdAt', 'desc')
      );

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (!snapshot.empty) {
            const data = snapshot.docs.map((doc, idx) => ({
              id: doc.id,
              views: (doc.data() as any).views || (1200 + (snapshot.size - idx) * 350),
              isPopular: idx < 3 || (doc.data() as any).isPopular,
              ...doc.data()
            } as ArticleItem));
            setArticles(data);
          } else {
            setArticles(FALLBACK_ARTICLES);
          }
          setLoading(false);
        },
        (err) => {
          console.warn("Gagal memuat artikel realtime, menggunakan data fallback:", err);
          setArticles(FALLBACK_ARTICLES);
          setLoading(false);
        }
      );
    } catch (e) {
      console.warn("Inisialisasi Firestore articles error:", e);
      setArticles(FALLBACK_ARTICLES);
      setLoading(false);
    }

    return () => unsubscribe();
  }, []);

  const displayedList = articles.length > 0 ? articles : FALLBACK_ARTICLES;

  // Filter based on selected category / tab
  const filteredArticles = displayedList.filter((item) => {
    if (activeTab === 'popular') return item.isPopular || (item.views && item.views > 2000);
    if (activeTab === 'smkk') return item.category.toLowerCase().includes('smkk') || item.category.toLowerCase().includes('regulasi');
    if (activeTab === 'legalitas') return item.category.toLowerCase().includes('sbu') || item.category.toLowerCase().includes('legalitas') || item.category.toLowerCase().includes('tender');
    if (activeTab === 'rab') return item.category.toLowerCase().includes('rab') || item.category.toLowerCase().includes('biaya') || item.category.toLowerCase().includes('manajemen');
    return true;
  });

  const featuredArticle = filteredArticles[0] || displayedList[0];
  const sideArticles = filteredArticles.slice(1, 4).length > 0 
    ? filteredArticles.slice(1, 4) 
    : displayedList.slice(1, 4);

  return (
    <section className="py-24 bg-background border-t border-border/50 relative overflow-hidden transition-colors duration-300">
      {/* Subtle Background Glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[350px] bg-gold/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold text-xs font-black uppercase tracking-widest mb-3">
              <Flame className="h-3.5 w-3.5 fill-current" />
              Update Konstruksi Terkini & Populer
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-foreground tracking-tight leading-tight">
              Wawasan & <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-gold-light">Berita Konstruksi</span>
            </h2>
            <p className="text-foreground/60 text-base sm:text-lg max-w-2xl mt-2 font-medium">
              Kompilasi panduan teknis, rilis standar Kementerian PUPR, tips lolos tender LPSE, dan manajemen biaya proyek.
            </p>
          </div>

          {/* View All Button */}
          <Button asChild variant="outline" className="border-border hover:border-gold/40 text-foreground hover:text-gold rounded-xl font-bold h-11 px-5 self-start md:self-auto shrink-0 shadow-sm">
            <Link to="/articles" className="inline-flex items-center gap-2">
              <span>Buka Semua Artikel</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        {/* Filter Tabs / Segmented Controls */}
        <div className="flex flex-wrap items-center gap-2 pb-8 border-b border-border/60 mb-10">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${
              activeTab === 'all'
                ? 'bg-gold text-navy shadow-md shadow-gold/20 scale-105'
                : 'bg-card text-foreground/70 hover:text-foreground hover:bg-muted border border-border/60'
            }`}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Semua Update
          </button>
          <button
            onClick={() => setActiveTab('popular')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${
              activeTab === 'popular'
                ? 'bg-gold text-navy shadow-md shadow-gold/20 scale-105'
                : 'bg-card text-foreground/70 hover:text-foreground hover:bg-muted border border-border/60'
            }`}
          >
            <Flame className="h-3.5 w-3.5 text-orange-500" />
            Berita Populer
          </button>
          <button
            onClick={() => setActiveTab('smkk')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${
              activeTab === 'smkk'
                ? 'bg-gold text-navy shadow-md shadow-gold/20 scale-105'
                : 'bg-card text-foreground/70 hover:text-foreground hover:bg-muted border border-border/60'
            }`}
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Regulasi & SMKK
          </button>
          <button
            onClick={() => setActiveTab('legalitas')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${
              activeTab === 'legalitas'
                ? 'bg-gold text-navy shadow-md shadow-gold/20 scale-105'
                : 'bg-card text-foreground/70 hover:text-foreground hover:bg-muted border border-border/60'
            }`}
          >
            <Building2 className="h-3.5 w-3.5" />
            Tender & Legalitas
          </button>
          <button
            onClick={() => setActiveTab('rab')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${
              activeTab === 'rab'
                ? 'bg-gold text-navy shadow-md shadow-gold/20 scale-105'
                : 'bg-card text-foreground/70 hover:text-foreground hover:bg-muted border border-border/60'
            }`}
          >
            <TrendingUp className="h-3.5 w-3.5" />
            Manajemen RAB
          </button>
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div className="py-20 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gold mx-auto mb-3" />
            <p className="text-foreground/50 text-sm font-medium">Memuat artikel dan wawasan konstruksi terbaru...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Spotlight / Featured Main Card (7 Cols) */}
            {featuredArticle && (
              <div className="lg:col-span-7 flex">
                <Card className="w-full bg-card border-border/80 hover:border-gold/50 shadow-md hover:shadow-2xl hover:shadow-gold/5 transition-all duration-300 rounded-3xl overflow-hidden flex flex-col p-0 group">
                  
                  {/* Visual Header with Scrim */}
                  <div className="relative aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden bg-slate-900">
                    <img 
                      src={featuredArticle.imageUrl || 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1200&q=80'} 
                      alt={featuredArticle.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    
                    {/* Badge Overlay */}
                    <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
                      <span className="px-3 py-1 rounded-lg text-xs font-black bg-gold text-navy shadow-md">
                        {featuredArticle.category}
                      </span>
                      <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-navy/80 text-white backdrop-blur-md border border-white/20 flex items-center gap-1">
                        <Flame className="h-3 w-3 text-gold fill-current" />
                        Sorotan Utama
                      </span>
                    </div>

                    {/* Views metric badge */}
                    {featuredArticle.views && (
                      <div className="absolute bottom-4 right-4 z-10 px-3 py-1 rounded-lg text-xs font-bold bg-black/60 backdrop-blur-md text-white/90 border border-white/10 flex items-center gap-1.5">
                        <TrendingUp className="h-3.5 w-3.5 text-gold" />
                        <span>{featuredArticle.views.toLocaleString('id-ID')} Pembaca</span>
                      </div>
                    )}
                  </div>

                  {/* Body Content */}
                  <CardHeader className="p-6 sm:p-8 pb-3">
                    <div className="flex items-center gap-3 text-xs text-foreground/50 font-medium mb-3">
                      <span className="flex items-center gap-1 text-gold font-bold">
                        <User className="h-3.5 w-3.5" />
                        {featuredArticle.authorName || 'Admin Azman204'}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDateSafe(featuredArticle.createdAt, { day: 'numeric', month: 'long', year: 'numeric' }, 'Terbaru 2026')}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        5 Menit Baca
                      </span>
                    </div>

                    <CardTitle className="text-2xl sm:text-3xl font-black text-foreground group-hover:text-gold transition-colors leading-tight line-clamp-2">
                      <Link to={`/article/${featuredArticle.slug}`}>
                        {featuredArticle.title}
                      </Link>
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 sm:px-8 pt-0 flex-grow">
                    <CardDescription className="text-foreground/70 text-sm sm:text-base leading-relaxed line-clamp-3">
                      {featuredArticle.excerpt}
                    </CardDescription>
                  </CardContent>

                  <CardFooter className="p-6 sm:p-8 pt-4 bg-muted/20 border-t border-border/50 flex items-center justify-between">
                    <Button asChild className="bg-gold hover:bg-gold-light text-navy font-bold rounded-xl px-5 h-11 shadow-md shadow-gold/10">
                      <Link to={`/article/${featuredArticle.slug}`} className="flex items-center gap-2">
                        <span>Baca Selengkapnya</span>
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    <span className="text-xs font-bold text-foreground/40 uppercase tracking-widest hidden sm:inline">
                      Edisi Konstruksi 2026
                    </span>
                  </CardFooter>
                </Card>
              </div>
            )}

            {/* Side Trending Stack (5 Cols) */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              <div className="px-2 pb-1 flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-wider text-foreground/50 flex items-center gap-1.5">
                  <TrendingUp className="h-4 w-4 text-gold" />
                  Paling Banyak Dipelajari
                </span>
                <span className="text-[11px] text-foreground/40 font-medium">3 Artikel Terpilih</span>
              </div>

              {sideArticles.map((article, idx) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group block"
                >
                  <Card className="bg-card border-border/70 hover:border-gold/40 shadow-sm hover:shadow-xl hover:shadow-gold/5 transition-all duration-300 rounded-2xl overflow-hidden p-4 sm:p-5 flex flex-row items-center gap-4 group">
                    {/* Thumbnail */}
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden shrink-0 bg-slate-900 relative">
                      <img 
                        src={article.imageUrl || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=300&q=80'} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        referrerPolicy="no-referrer"
                      />
                      <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-black bg-navy/90 text-gold backdrop-blur-sm border border-gold/20">
                        0{idx + 1}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[11px] text-foreground/50 font-medium mb-1">
                        <span className="text-gold font-bold truncate max-w-[120px]">
                          {article.category}
                        </span>
                        <span>•</span>
                        <span>{formatDateSafe(article.createdAt, { day: 'numeric', month: 'short' }, 'Terbaru')}</span>
                      </div>
                      <h4 className="text-sm sm:text-base font-bold text-foreground group-hover:text-gold transition-colors leading-snug line-clamp-2 mb-1.5">
                        {article.title}
                      </h4>
                      <p className="text-xs text-foreground/60 line-clamp-1 leading-relaxed hidden sm:block">
                        {article.excerpt}
                      </p>
                      <div className="inline-flex items-center text-[11px] font-black text-gold mt-1 group-hover:gap-1.5 transition-all">
                        <span>Pelajari</span>
                        <ChevronRight className="h-3 w-3" />
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}

              {/* Quick Knowledge Prompt Banner */}
              <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-navy via-slate-900 to-navy text-white border border-gold/20 shadow-md flex items-center justify-between gap-4 mt-auto">
                <div className="space-y-0.5">
                  <span className="text-[10px] font-black text-gold uppercase tracking-wider block">Koleksi Format Tender</span>
                  <p className="text-xs text-white/80 font-medium">Download template Excel SMKK, RKK, & RAB lengkap.</p>
                </div>
                <Button asChild size="sm" className="bg-gold hover:bg-gold-light text-navy font-bold rounded-xl shrink-0 text-xs shadow-sm">
                  <Link to="/products">
                    Lihat Template
                  </Link>
                </Button>
              </div>
            </div>

          </div>
        )}

      </div>
    </section>
  );
}
